import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Bell, ImagePlus, LoaderCircle, Plus, Trash2, UploadCloud } from 'lucide-react';
import {
  cleanupRemovedCloudinaryAssets,
  commitCloudinaryUploads,
  discardCloudinaryUploads,
  uploadToCloudinary,
} from '../services/adminApi';
import { buildManagedAssetList, buildMediaSnapshot, splitPendingUploads } from '../services/media';
import CategoryField from './CategoryField';
import DataConverter from './DataConverter';
import { Notice } from './UI';

const LANGUAGES = [
  ['gu', 'ગુજરાતી'],
  ['hi', 'हिन्दी'],
  ['en', 'English'],
];

function getInitialRecord(module, initialValue, generatedDocumentId = '') {
  const base = { id: generatedDocumentId, isActive: true, sortOrder: '' };
  module.fields.forEach((field) => {
    if (field.type === 'translated' || (field.type === 'category' && field.storage === 'translated')) {
      base[field.key] = { gu: '', hi: '', en: '' };
    }
    else if (field.type === 'list') base[field.key] = [];
    else if (field.type === 'toggle') base[field.key] = false;
    else if (field.type === 'number') base[field.key] = '';
    else base[field.key] = '';
  });
  return { ...base, ...structuredClone(initialValue || {}) };
}

function validateFields(fields, value, prefix = '') {
  const errors = [];
  fields.forEach((field) => {
    const fieldValue = value?.[field.key];
    const label = prefix ? `${prefix} ${field.label}` : field.label;
    if (field.required) {
      if (field.type === 'translated' || (field.type === 'category' && field.storage === 'translated')) {
        LANGUAGES.forEach(([language, languageLabel]) => {
          if (!String(fieldValue?.[language] || '').trim()) errors.push(`${label} (${languageLabel}) required chhe.`);
        });
      } else if (!String(fieldValue ?? '').trim()) {
        errors.push(`${label} required chhe.`);
      }
    }
    if (field.type === 'list' && field.itemFields) {
      (fieldValue || []).forEach((item, index) => {
        errors.push(...validateFields(field.itemFields, item, `${field.itemLabel || 'Item'} ${index + 1} –`));
      });
    }
  });
  return errors;
}

function TextField({ field, value, onChange }) {
  const Component = field.type === 'textarea' || field.multiline ? 'textarea' : 'input';
  return (
    <label className="field">
      <span>{field.label}{field.required ? <em>*</em> : null}</span>
      <Component
        type={field.inputType || 'text'}
        value={value ?? ''}
        min={field.min}
        placeholder={field.placeholder}
        rows={field.multiline || field.type === 'textarea' ? 4 : undefined}
        onChange={(event) => onChange(field.type === 'number' && event.target.value !== '' ? Number(event.target.value) : event.target.value)}
      />
      {field.hint ? <small>{field.hint}</small> : null}
    </label>
  );
}

function TranslatedField({ field, value = {}, onChange }) {
  return (
    <fieldset className="translated-group">
      <legend>{field.label}{field.required ? <em>*</em> : null}</legend>
      <div className="translation-grid">
        {LANGUAGES.map(([language, label]) => (
          <label className="field" key={language}>
            <span>{label}</span>
            {field.multiline ? (
              <textarea rows={4} value={value?.[language] || ''} onChange={(event) => onChange({ ...value, [language]: event.target.value })} />
            ) : (
              <input value={value?.[language] || ''} onChange={(event) => onChange({ ...value, [language]: event.target.value })} />
            )}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function ImageField({
  field,
  value,
  onChange,
  canUpload,
  interactionLocked,
  onUploadStarted,
  onUploadFinished,
  onAssetUploaded,
  onAssetRemoved,
  collectionName,
}) {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Fakt image file select karo.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Image 8 MB karta nani hovi joiye.');
      return;
    }

    setUploading(true);
    setError('');
    onUploadStarted?.();
    try {
      const asset = await uploadToCloudinary(file, collectionName);
      onChange(asset.url);
      onAssetUploaded?.(asset);
    } catch (uploadError) {
      setError(uploadError.message || 'Upload failed. URL manually paste kari shako cho.');
    } finally {
      setUploading(false);
      onUploadFinished?.();
    }
  };

  const handleRemove = async () => {
    if (!value) return;
    const targetUrl = value;
    setRemoving(true);
    setError('');
    try {
      if (onAssetRemoved) {
        await onAssetRemoved(targetUrl);
      }
      onChange('');
    } catch (removeError) {
      setError(removeError?.message || 'Failed to remove image.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="field image-field">
      <span>{field.label}</span>
      <div className="image-field-row">
        <div className="image-preview">
          {value ? (
            <img
              src={value}
              alt="Preview"
              onError={(event) => { event.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <ImagePlus size={24} />
          )}
        </div>
        <div className="image-controls">
          <input
            type="url"
            value={value || ''}
            placeholder="https://…"
            disabled={uploading || removing}
            onChange={(event) => onChange(event.target.value)}
          />
          <div className="image-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {canUpload ? (
              <label className={`button secondary compact ${uploading || removing || interactionLocked ? 'disabled' : ''}`}>
                {uploading ? <LoaderCircle className="spin" size={16} /> : <UploadCloud size={16} />}
                {uploading ? 'Uploading…' : value ? 'Replace image' : 'Upload image'}
                <input type="file" accept="image/*" hidden disabled={uploading || removing || interactionLocked} onChange={upload} />
              </label>
            ) : null}
            {value ? (
              <button
                type="button"
                className="button danger compact"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                disabled={uploading || removing || interactionLocked}
                onClick={handleRemove}
                title="Remove image and delete from Cloudinary"
              >
                {removing ? <LoaderCircle className="spin" size={16} /> : <Trash2 size={16} />}
                {removing ? 'Removing…' : 'Remove image'}
              </button>
            ) : null}
          </div>
        </div>
      </div>
      {uploading ? <small>Image upload thai rahi chhe. Save, Cancel ane popup close temporarily disabled chhe.</small> : null}
      {value ? <small style={{ color: '#5a685e' }}>Image remove kari record Save karta j Cloudinary mathi pan clean thai jase.</small> : null}
      {error ? <small className="field-error">{error}</small> : null}
    </div>
  );
}

function FieldInput({ field, value, onChange, mediaProps, categoryProps }) {
  if (field.type === 'translated') return <TranslatedField field={field} value={value} onChange={onChange} />;
  if (field.type === 'category') {
    return <CategoryField field={field} value={value} onChange={onChange} {...categoryProps} />;
  }
  if (field.type === 'image') return <ImageField field={field} value={value} onChange={onChange} {...mediaProps} />;
  if (field.type === 'select') {
    return (
      <label className="field">
        <span>{field.label}</span>
        <select value={value || ''} onChange={(event) => onChange(event.target.value)}>
          <option value="">Select…</option>
          {field.options.map(([optionValue, label]) => <option key={optionValue} value={optionValue}>{label}</option>)}
        </select>
      </label>
    );
  }
  if (field.type === 'toggle') {
    return (
      <label className="toggle-field">
        <input type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />
        <span className="toggle-track"><span /></span>
        <strong>{field.label}</strong>
      </label>
    );
  }
  return <TextField field={field} value={value} onChange={onChange} />;
}

function ListField({ field, value = [], onChange, mediaProps, categoryProps }) {
  const addItem = () => {
    if (field.primitive) onChange([...value, '']);
    else {
      const item = {};
      (field.itemFields || []).forEach((itemField) => {
        if (itemField.type === 'translated') item[itemField.key] = { gu: '', hi: '', en: '' };
        else if (itemField.type === 'list') item[itemField.key] = [];
        else if (itemField.type === 'toggle') item[itemField.key] = false;
        else item[itemField.key] = '';
      });
      onChange([...value, item]);
    }
  };

  const updateItem = (index, nextItem) => onChange(value.map((item, itemIndex) => itemIndex === index ? nextItem : item));
  const removeItem = async (index) => {
    const itemToRemove = value[index];
    if (typeof itemToRemove === 'string' && itemToRemove) {
      await mediaProps?.onAssetRemoved?.(itemToRemove);
    }
    onChange(value.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <fieldset className="list-group">
      <legend>{field.label}</legend>
      <div className="list-items">
        {value.map((item, index) => (
          <section className="nested-card" key={`${field.key}-${index}`}>
            <header>
              <strong>{field.itemLabel || 'Item'} {index + 1}</strong>
              <button type="button" className="icon-button danger-ghost" onClick={() => removeItem(index)} aria-label="Remove item"><Trash2 size={17} /></button>
            </header>
            {field.primitive ? (
              field.itemType === 'image' ? (
                <ImageField field={{ label: field.itemLabel || 'Image' }} value={item} onChange={(next) => updateItem(index, next)} {...mediaProps} />
              ) : (
                <TextField field={{ label: field.itemLabel || 'Value', type: 'text', placeholder: field.placeholder }} value={item} onChange={(next) => updateItem(index, next)} />
              )
            ) : (
              <div className="nested-fields">
                {field.itemFields.map((itemField) => (
                  <FieldInput
                    key={itemField.key}
                    field={itemField}
                    value={item?.[itemField.key]}
                    onChange={(next) => updateItem(index, { ...item, [itemField.key]: next })}
                    mediaProps={mediaProps}
                    categoryProps={categoryProps}
                  />
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
      <button className="button secondary compact" type="button" onClick={addItem}><Plus size={16} /> Add {field.itemLabel || 'item'}</button>
    </fieldset>
  );
}

const FormRenderer = forwardRef(function FormRenderer({
  module,
  initialValue,
  onSubmit,
  onSaved,
  onCancel,
  onInteractionLockChange,
  busy,
  isNew = false,
  canUpload = false,
  fixedDocumentId,
  generatedDocumentId = '',
  categoryRecords = [],
  canDeleteCategory = false,
}, ref) {
  const initial = useMemo(
    () => getInitialRecord(module, initialValue, generatedDocumentId),
    [generatedDocumentId, initialValue, module],
  );
  const [record, setRecord] = useState(initial);
  const [errors, setErrors] = useState([]);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [activeUploads, setActiveUploads] = useState(0);
  const [mediaBusy, setMediaBusy] = useState(false);

  useEffect(() => {
    if (!isNew || !generatedDocumentId) return;
    setRecord((current) => current.id === generatedDocumentId
      ? current
      : { ...current, id: generatedDocumentId });
  }, [generatedDocumentId, isNew]);

  const interactionLocked = Boolean(busy || mediaBusy || activeUploads > 0);

  useEffect(() => {
    onInteractionLockChange?.(interactionLocked);
    return () => onInteractionLockChange?.(false);
  }, [interactionLocked, onInteractionLockChange]);

  useEffect(() => {
    if (!pendingUploads.length && activeUploads === 0) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [activeUploads, pendingUploads.length]);

  const update = (key, value) => setRecord((current) => ({ ...current, [key]: value }));
  const uploadStarted = () => setActiveUploads((count) => count + 1);
  const uploadFinished = () => setActiveUploads((count) => Math.max(0, count - 1));
  const assetUploaded = (asset) => setPendingUploads((current) => [
    ...current.filter((item) => item.uploadId !== asset.uploadId),
    asset,
  ]);
  const assetRemoved = async (url) => {
    if (!url) return;
    const pending = pendingUploads.find((item) => item.url === url);
    if (pending?.uploadId) {
      try {
        await discardCloudinaryUploads([pending.uploadId]);
      } catch (err) {
        console.warn('Failed to discard pending Cloudinary upload:', err);
      }
      setPendingUploads((current) => current.filter((item) => item.url !== url));
    }
  };

  const cancel = useCallback(async () => {
    if (interactionLocked) return false;
    setMediaBusy(true);
    try {
      const uploadIds = pendingUploads.map((asset) => asset.uploadId).filter(Boolean);
      if (uploadIds.length) await discardCloudinaryUploads(uploadIds);
    } catch {
      // Scheduled orphan cleanup is the fallback when the browser is offline.
    } finally {
      setPendingUploads([]);
      setMediaBusy(false);
      onCancel?.();
    }
    return true;
  }, [interactionLocked, onCancel, pendingUploads]);

  useImperativeHandle(ref, () => ({ cancel, interactionLocked }), [cancel, interactionLocked]);

  const submit = async (event) => {
    event.preventDefault();
    if (interactionLocked) return;

    const nextErrors = validateFields(module.fields, record);
    if (isNew && !fixedDocumentId && !String(record.id || '').trim()) nextErrors.push('Document ID required chhe.');
    setErrors(nextErrors);
    if (nextErrors.length) return;

    const recordWithMedia = {
      ...record,
      ...(fixedDocumentId ? { id: fixedDocumentId } : {}),
      sortOrder: record.sortOrder === '' ? undefined : Number(record.sortOrder),
    };
    recordWithMedia._cloudinaryAssets = buildManagedAssetList(
      recordWithMedia,
      pendingUploads,
      initialValue?._cloudinaryAssets,
    );

    setMediaBusy(true);
    try {
      const saved = await onSubmit(recordWithMedia);
      const { used, unused } = splitPendingUploads(recordWithMedia, pendingUploads);
      const cleanupTasks = [];
      if (used.length) cleanupTasks.push(commitCloudinaryUploads(used.map((asset) => asset.uploadId).filter(Boolean)));
      if (unused.length) cleanupTasks.push(discardCloudinaryUploads(unused.map((asset) => asset.uploadId).filter(Boolean)));
      cleanupTasks.push(cleanupRemovedCloudinaryAssets({
        collectionName: module.collection,
        documentId: saved?.id || recordWithMedia.id,
        beforeMedia: buildMediaSnapshot(initialValue),
        afterMedia: buildMediaSnapshot(recordWithMedia),
      }));
      await Promise.allSettled(cleanupTasks);
      setPendingUploads([]);
      setErrors([]);
      onSaved?.(saved);
    } catch (saveError) {
      setErrors([saveError.message || 'Save failed.']);
    } finally {
      setMediaBusy(false);
    }
  };

  const isGeneratedId = isNew && Boolean(generatedDocumentId);
  const mediaProps = {
    canUpload,
    interactionLocked,
    onUploadStarted: uploadStarted,
    onUploadFinished: uploadFinished,
    onAssetUploaded: assetUploaded,
    onAssetRemoved: assetRemoved,
    collectionName: module.collection,
  };
  const categoryProps = {
    moduleKey: module.collection,
    records: categoryRecords,
    canDelete: canDeleteCategory,
    interactionLocked,
  };

  return (
    <form className="record-form" onSubmit={submit}>
      {errors.length ? (
        <Notice type="error"><span>{errors[0]}{errors.length > 1 ? ` (+${errors.length - 1} more)` : ''}</span></Notice>
      ) : null}

      <DataConverter
        module={module}
        record={record}
        documentId={fixedDocumentId || record.id || generatedDocumentId}
        interactionLocked={interactionLocked}
        onApply={(nextRecord) => {
          setRecord(nextRecord);
          setErrors([]);
        }}
      />

      {!fixedDocumentId ? (
        <div className="form-grid two">
          <label className="field">
            <span>Document ID{isNew ? <em>*</em> : null}</span>
            <input
              value={record.id || ''}
              disabled={!isNew}
              readOnly={isGeneratedId}
              placeholder="example: b7"
              onChange={isGeneratedId ? undefined : (event) => update('id', event.target.value.replace(/\s+/g, '-'))}
            />
            <small>{isGeneratedId ? 'System generated ID chhe. Save pela change karvani jarur nathi.' : 'Create pachhi ID change nahi thai shake.'}</small>
          </label>
          <label className="field">
            <span>Sort order</span>
            <input type="number" min="1" value={record.sortOrder ?? ''} onChange={(event) => update('sortOrder', event.target.value)} />
          </label>
        </div>
      ) : null}

      {(() => {
        const NOTIFICATION_FIELD_KEYS = ['sendPush', 'sendUpdateNotification', 'notificationTitle', 'notificationText'];
        const contentFields = module.fields.filter((field) => !NOTIFICATION_FIELD_KEYS.includes(field.key));
        const pushFields = module.fields.filter((field) => NOTIFICATION_FIELD_KEYS.includes(field.key));

        return (
          <>
            {contentFields.map((field) => (
              field.type === 'list' ? (
                <ListField key={field.key} field={field} value={record[field.key]} onChange={(value) => update(field.key, value)} mediaProps={mediaProps} categoryProps={categoryProps} />
              ) : (
                <FieldInput key={field.key} field={field} value={record[field.key]} onChange={(value) => update(field.key, value)} mediaProps={mediaProps} categoryProps={categoryProps} />
              )
            ))}

            {pushFields.length ? (
              <section
                style={{
                  margin: '18px 0',
                  padding: '16px 18px',
                  borderRadius: '12px',
                  border: '1.5px solid #7dd3fc',
                  backgroundColor: '#f0f9ff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', color: '#0369a1', fontWeight: '700', fontSize: '15px' }}>
                  <Bell size={19} color="#0284c7" />
                  <span>Push Notification Settings</span>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#0284c7', background: '#e0f2fe', padding: '2px 8px', borderRadius: '999px', border: '1px solid #bae6fd' }}>
                    OneSignal Push
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {pushFields.map((field) => (
                    <FieldInput key={field.key} field={field} value={record[field.key]} onChange={(value) => update(field.key, value)} mediaProps={mediaProps} categoryProps={categoryProps} />
                  ))}
                </div>
              </section>
            ) : null}
          </>
        );
      })()}

      <label className="toggle-field publish-toggle">
        <input type="checkbox" checked={record.isActive !== false} onChange={(event) => update('isActive', event.target.checked)} />
        <span className="toggle-track"><span /></span>
        <span><strong>Active / Published</strong><small>Active data mobile app ma immediately dekhase.</small></span>
      </label>

      <div className="form-actions sticky-actions">
        <button className="button secondary" type="button" onClick={cancel} disabled={interactionLocked}>Cancel</button>
        <button className="button primary" type="submit" disabled={interactionLocked}>
          {interactionLocked ? <LoaderCircle className="spin" size={17} /> : null}
          {activeUploads > 0 ? 'Uploading image…' : mediaBusy && !busy ? 'Finalizing media…' : busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
});

export default FormRenderer;
